import { useRef, useState } from 'react';
import {
  Button,
  Checkbox,
  Divider,
  Group,
  Image,
  Select,
  Stack,
  Text,
  Textarea,
  TextInput,
  Title,
} from '@mantine/core';
import { DateInput, TimeInput } from '@mantine/dates';
import { MIME_TYPES } from '@mantine/dropzone';
import { FormValidateInput, useForm } from '@mantine/form';
import {
  IconArrowLeft,
  IconArrowRight,
  IconCheck,
  IconEraser,
  IconPhoto,
} from '@tabler/icons-react';
import { zodResolver } from 'mantine-form-zod-resolver';
import SignatureCanvas from 'react-signature-canvas';
import { z } from 'zod';

import { RouterOutput } from '../../../../../../services/trpc';
import {
  getUTDocumentUrl,
  useUploadThing,
} from '../../../../../../services/upload';
import { handleFormError } from '../../../../../../utils/handle-form-error';
import { useSignatureCanvas } from '../../poseur/-use-signature-canvas';
import { DeletableImage } from '../technical-plan/-deletable-image';
import { FileUploadButton } from '../technical-plan/-file-upload-button';

interface YesNoQuestionProps {
  label: string;
  value: boolean | undefined;
  onChange: (value: boolean) => void;
}

function YesNoQuestion({ label, value, onChange }: YesNoQuestionProps) {
  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        width: '100%',
        gap: 16,
      }}
    >
      <span style={{ flex: 1 }}>{label}</span>
      <div
        style={{
          display: 'flex',
          gap: 16,
          minWidth: 80,
        }}
      >
        <Checkbox
          styles={{ label: { paddingLeft: 4 } }}
          label="Oui"
          checked={value === true}
          onChange={() => onChange(true)}
        />
        <Checkbox
          styles={{ label: { paddingLeft: 4 } }}
          label="Non"
          checked={value === false}
          onChange={() => onChange(false)}
        />
      </div>
    </div>
  );
}

const serviceOrderFormSchema = z.object({
  date: z.string(),
  time: z.string().optional(),
  attendees: z.array(z.string()),
  extraAttendee: z.string(),
  answer1: z.boolean().optional(),
  answer2: z.boolean().optional(),
  answer3: z.boolean().optional(),
  answer4: z.boolean().optional(),
  answer5: z.boolean().optional(),
  answer6: z.boolean().optional(),
  observations: z.string().optional(),
  signer: z.string(),
  signatureUtFileKey: z.string(),
  imagesUtFileKeys: z.array(z.string()),
});

export type ServiceOrderFormValues = z.infer<typeof serviceOrderFormSchema>;

export function ServiceOrderForm({
  project,
  serviceOrderRequest,
  onSubmit,
}: {
  project: RouterOutput['projects']['get'];
  serviceOrderRequest: NonNullable<
    RouterOutput['projects']['get']
  >['serviceOrderRequests'][0];
  onSubmit: (values: ServiceOrderFormValues) => void;
}) {
  const { startUpload } = useUploadThing('signatureUploader');
  const [step, setStep] = useState<'form' | 'photos' | 'signature'>('form');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const signatureRef = useRef<SignatureCanvas>(null);
  const { handleSignatureDraw, handleSignatureClear } = useSignatureCanvas();

  const client1Name = `${project.customer.customer1Title} ${project.customer.customer1FirstName} ${project.customer.customer1LastName}`;
  const client2Name = project.customer.customer2FirstName
    ? `${project.customer.customer2Title} ${project.customer.customer2FirstName} ${project.customer.customer2LastName}`
    : null;

  const toggleAttendee = (name: string) => {
    const currentAttendees = form.values.attendees;
    if (currentAttendees.includes(name)) {
      form.setFieldValue(
        'attendees',
        currentAttendees.filter(a => a !== name),
      );
    } else {
      form.setFieldValue('attendees', [...currentAttendees, name]);
    }
  };

  const form = useForm<ServiceOrderFormValues>({
    initialValues: {
      date: '',
      time: undefined,
      attendees: [] as string[],
      extraAttendee: '',
      answer1: undefined,
      answer2: undefined,
      answer3: undefined,
      answer4: undefined,
      answer5: undefined,
      answer6: undefined,
      observations: '',
      signer: '',
      signatureUtFileKey: '',
      imagesUtFileKeys: [],
    },
    validate: zodResolver(
      serviceOrderFormSchema,
    ) as FormValidateInput<ServiceOrderFormValues>,
  });

  const isFormValid = () => {
    return (
      (!serviceOrderRequest.question1 || form.values.answer1 !== undefined) &&
      (!serviceOrderRequest.question2 || form.values.answer2 !== undefined) &&
      (!serviceOrderRequest.question3 || form.values.answer3 !== undefined) &&
      (!serviceOrderRequest.question4 || form.values.answer4 !== undefined) &&
      (!serviceOrderRequest.question5 || form.values.answer5 !== undefined) &&
      (!serviceOrderRequest.question6 || form.values.answer6 !== undefined)
    );
  };

  const nbRequiredImages = 3;
  const hasRequiredNbOfImages =
    form.values.imagesUtFileKeys.length >= nbRequiredImages;

  const hasAttendees =
    form.values.attendees.length > 0 || form.values.extraAttendee.trim() !== '';

  const handleSubmit = async (values: ServiceOrderFormValues) => {
    if (step === 'form') {
      if (!isFormValid()) {
        return;
      }
      setStep('photos');
      return;
    }

    if (step === 'photos') {
      if (!hasRequiredNbOfImages) {
        return;
      }
      if (hasAttendees) {
        setStep('signature');
      } else {
        // No attendees, skip signature and submit directly
        setIsSubmitting(true);
        try {
          // Set empty signer and signature for no-attendee case
          values.signer = '';
          values.signatureUtFileKey = '';
          await onSubmit(values);
        } catch (error) {
          handleFormError(form, error);
          setIsSubmitting(false);
        }
      }
      return;
    }

    if (step === 'signature') {
      if (!values.signer) {
        window.alert('Veuillez sélectionner un signataire');
        return;
      }
      const signature = signatureRef.current?.toDataURL();
      if (!signature) {
        form.setFieldError('signatureUtFileKey', 'La signature est requise');
        return;
      }

      setIsSubmitting(true);

      try {
        const response = await fetch(signature);
        const blob = await response.blob();
        const file = new File([blob], 'signature.png', { type: 'image/png' });

        const uploadResult = await startUpload([file]);
        if (uploadResult && uploadResult.length > 0) {
          values.signatureUtFileKey = uploadResult[0].key;
        } else {
          throw new Error('Échec du téléchargement de la signature');
        }

        await onSubmit(values);
      } catch (error) {
        handleFormError(form, error);
        setIsSubmitting(false);
      }
    }
  };

  if (step === 'photos') {
    return (
      <form onSubmit={form.onSubmit(handleSubmit)}>
        <Stack gap="xl">
          <Title order={4}>Photos</Title>
          <Stack gap="md">
            <Stack align="center">
              <FileUploadButton
                buttonProps={{
                  variant: hasRequiredNbOfImages ? 'outline' : 'filled',
                  size: 'lg',
                  leftSection: <IconPhoto size={16} />,
                }}
                placeholder="Ajouter des photos"
                accept={`${MIME_TYPES.png},${MIME_TYPES.jpeg},${MIME_TYPES.webp}`}
                utUploadKey="constructionSiteReportImagesUploader"
                multiple
                onChange={files =>
                  form.setFieldValue('imagesUtFileKeys', [
                    ...form.values.imagesUtFileKeys,
                    ...files.map(f => f.key),
                  ])
                }
              />
              {!hasRequiredNbOfImages && (
                <Text c="red">
                  Veuillez ajouter au moins {nbRequiredImages} photos pour
                  valider.
                </Text>
              )}
            </Stack>
            {form.values.imagesUtFileKeys.length > 0 && (
              <Group gap="sm">
                {form.values.imagesUtFileKeys.map(key => (
                  <DeletableImage
                    key={key}
                    onDelete={() => {
                      form.setFieldValue(
                        'imagesUtFileKeys',
                        form.values.imagesUtFileKeys.filter(k => k !== key),
                      );
                    }}
                  >
                    <Image
                      src={getUTDocumentUrl(key)}
                      height={70}
                      width="auto"
                      fit="contain"
                      style={{ maxHeight: '100px' }}
                    />
                  </DeletableImage>
                ))}
              </Group>
            )}
          </Stack>

          <Divider />

          <Group justify="space-between">
            <Button
              leftSection={<IconArrowLeft size={16} />}
              variant="outline"
              disabled={isSubmitting}
              onClick={() => setStep('form')}
            >
              Retour
            </Button>
            <Group justify="flex-end" gap="xs">
              {hasAttendees ? (
                <Button
                  leftSection={<IconArrowRight size={16} />}
                  loading={isSubmitting}
                  disabled={!hasRequiredNbOfImages}
                  onClick={() => setStep('signature')}
                >
                  Continuer
                </Button>
              ) : (
                <Button
                  leftSection={<IconCheck size={16} />}
                  type="submit"
                  loading={isSubmitting}
                  disabled={!hasRequiredNbOfImages}
                >
                  Valider
                </Button>
              )}
            </Group>
          </Group>
        </Stack>
      </form>
    );
  }

  if (step === 'signature') {
    const allAttendees = [
      ...form.values.attendees,
      ...(form.values.extraAttendee ? [form.values.extraAttendee] : []),
    ].filter(Boolean);

    return (
      <form onSubmit={form.onSubmit(handleSubmit)}>
        <Stack gap="xl">
          <Title order={3}>Signature</Title>

          <Select
            allowDeselect={false}
            required
            label="Signataire"
            placeholder="Nom du signataire"
            value={form.values.signer}
            data={allAttendees.map(attendee => ({
              label: attendee,
              value: attendee,
            }))}
            onChange={value => {
              form.setFieldValue('signer', value ?? '');
            }}
          />

          <Stack gap="md">
            <div
              style={{
                border: '1px solid #ced4da',
                borderRadius: '4px',
                padding: '8px',
              }}
            >
              <SignatureCanvas
                ref={signatureRef}
                onEnd={handleSignatureDraw}
                canvasProps={{
                  width: 500,
                  height: 200,
                  className: 'signature-canvas',
                }}
              />
            </div>

            <Group>
              <Button
                variant="outline"
                leftSection={<IconEraser size={16} />}
                onClick={() => {
                  handleSignatureClear();
                  signatureRef.current?.clear();
                }}
              >
                Effacer
              </Button>
            </Group>
          </Stack>

          <Group justify="space-between">
            <Button
              variant="outline"
              leftSection={<IconArrowLeft size={16} />}
              onClick={() => setStep('photos')}
              disabled={isSubmitting}
            >
              Retour
            </Button>
            <Button
              type="submit"
              leftSection={<IconCheck size={16} />}
              loading={isSubmitting}
            >
              Valider
            </Button>
          </Group>
        </Stack>
      </form>
    );
  }

  return (
    <form onSubmit={form.onSubmit(handleSubmit)}>
      <Stack gap="xl">
        <div className="flex gap-2">
          <DateInput
            withWeekNumbers
            lang="fr"
            label="Date"
            required
            valueFormat="LL"
            // defaultValue={serviceOrderRequest.date ?? undefined}
            {...form.getInputProps('date')}
          />
          <TimeInput
            label="Heure"
            placeholder="HH:mm"
            type="time"
            // defaultValue={serviceOrderRequest.time ?? undefined}
            {...form.getInputProps('time')}
          />
        </div>

        <Stack gap={4}>
          <p>Personnes présentes:</p>
          <Checkbox
            label={client1Name}
            checked={form.values.attendees.includes(client1Name)}
            onChange={() => toggleAttendee(client1Name)}
          />
          {client2Name && (
            <Checkbox
              label={client2Name}
              checked={form.values.attendees.includes(client2Name)}
              onChange={() => toggleAttendee(client2Name)}
            />
          )}
          {project.customer.contacts.map(contact => {
            const name = `${contact.firstName} ${contact.lastName}`;
            return (
              <Checkbox
                key={contact.id}
                label={name}
                checked={form.values.attendees.includes(name)}
                onChange={() => toggleAttendee(name)}
              />
            );
          })}

          <Group>
            <TextInput
              placeholder="Autre personne présente"
              value={form.values.extraAttendee}
              onChange={event =>
                form.setFieldValue('extraAttendee', event.currentTarget.value)
              }
            />
          </Group>
        </Stack>

        <Stack gap="md">
          {serviceOrderRequest.question1 && (
            <YesNoQuestion
              label={serviceOrderRequest.question1}
              value={form.values.answer1}
              onChange={value => form.setFieldValue('answer1', value)}
            />
          )}
          {serviceOrderRequest.question2 && (
            <YesNoQuestion
              label={serviceOrderRequest.question2}
              value={form.values.answer2}
              onChange={value => form.setFieldValue('answer2', value)}
            />
          )}
          {serviceOrderRequest.question3 && (
            <YesNoQuestion
              label={serviceOrderRequest.question3}
              value={form.values.answer3}
              onChange={value => form.setFieldValue('answer3', value)}
            />
          )}
          {serviceOrderRequest.question4 && (
            <YesNoQuestion
              label={serviceOrderRequest.question4}
              value={form.values.answer4}
              onChange={value => form.setFieldValue('answer4', value)}
            />
          )}
          {serviceOrderRequest.question5 && (
            <YesNoQuestion
              label={serviceOrderRequest.question5}
              value={form.values.answer5}
              onChange={value => form.setFieldValue('answer5', value)}
            />
          )}
          {serviceOrderRequest.question6 && (
            <YesNoQuestion
              label={serviceOrderRequest.question6}
              value={form.values.answer6}
              onChange={value => form.setFieldValue('answer6', value)}
            />
          )}
        </Stack>

        <Textarea
          label="Observations"
          placeholder="Observations supplémentaires (optionnel)"
          minRows={3}
          {...form.getInputProps('observations')}
        />

        <Group justify="flex-end">
          <Button
            type="submit"
            rightSection={<IconArrowRight size={16} />}
            disabled={isSubmitting || !isFormValid()}
          >
            Suivant
          </Button>
        </Group>
      </Stack>
    </form>
  );
}
